import React, { useEffect, useState } from "react";
import { Briefcase, Plus, Edit3, Trash2, Save, X, ShieldCheck, Building2, Globe, Lock } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const SCOPES = [
  { value: "branch", label_ar: "فروع", label_en: "Branches", Icon: Building2, cls: "bg-teal-100 text-teal-800 border-teal-300" },
  { value: "admin", label_ar: "الإدارة", label_en: "Admin", Icon: ShieldCheck, cls: "bg-slate-800 text-white border-slate-900" },
  { value: "all", label_ar: "الكل", label_en: "All", Icon: Globe, cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
];

export default function Positions() {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, name, scope, order }
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", scope: "branch" });
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/positions");
      setItems(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const scopeInfo = (val) => SCOPES.find((s) => s.value === val) || SCOPES[0];
  const startAdd = () => { setCreating(true); setEditing(null); setForm({ name: "", scope: "branch" }); setErr(""); };
  const startEdit = (p) => { setCreating(false); setEditing(p); setForm({ name: p.name, scope: p.scope || "branch" }); setErr(""); };
  const cancel = () => { setCreating(false); setEditing(null); setErr(""); };

  const save = async () => {
    setErr("");
    const name = (form.name || "").trim();
    if (!name) { setErr(t("position_name_required")); return; }
    try {
      if (editing?.id) {
        await api.put(`/positions/${editing.id}`, form);
      } else {
        await api.post("/positions", form);
      }
      cancel();
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("delete_position_confirm").replace("{name}", p.name))) return;
    try {
      await api.delete(`/positions/${p.id}`);
      load();
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="positions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-teal-900 flex items-center gap-2">
            <Briefcase size={22} /> {t("nav_positions")}
          </h2>
          <p className="text-gray-500 text-sm">{t("positions_subtitle")}</p>
        </div>
        <button className="btn btn-primary" onClick={startAdd} data-testid="add-position-button">
          <Plus size={16} /> {t("add_position")}
        </button>
      </div>

      {/* Add / Edit form */}
      {(creating || editing) && (
        <div className="bg-white rounded-2xl card-shadow p-5 border-2 border-teal-200" data-testid="position-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-teal-900">
              {editing ? t("edit_position") : t("add_position")}
            </h3>
            <button className="btn btn-ghost" onClick={cancel}><X size={16} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="inp-label">{t("position_name")} (English)</label>
              <input
                className="inp"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Store Manager"
                dir="ltr"
                data-testid="position-name-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">{t("position_name_hint")}</p>
            </div>
            <div>
              <label className="inp-label">{t("position_scope")}</label>
              <select
                className="inp"
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                data-testid="position-scope-select"
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>{lang === "ar" ? s.label_ar : s.label_en}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">{t("position_scope_hint")}</p>
            </div>
          </div>
          {err && <div className="text-sm bg-rose-50 text-rose-700 rounded-lg p-2 mt-3">{err}</div>}
          <div className="mt-4 flex justify-end gap-2">
            <button className="btn btn-secondary" onClick={cancel}>{t("cancel")}</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-position-button">
              <Save size={14} /> {t("save")}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-2xl card-shadow">
        {loading ? (
          <div className="p-8 text-center text-gray-400">…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-400">{t("no_positions")}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((p) => {
              const s = scopeInfo(p.scope);
              return (
                <div key={p.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`position-row-${p.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center text-teal-700 shrink-0">
                      <Briefcase size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-slate-800 flex items-center gap-2" style={{ direction: "ltr" }}>
                        {p.name}
                        {p.is_default && (
                          <span className="pill bg-slate-100 text-slate-600 text-[10px]">
                            <Lock size={10} /> {t("default")}
                          </span>
                        )}
                      </div>
                      <div className="mt-1">
                        <span className={`pill inline-flex items-center gap-1 border ${s.cls}`}>
                          <s.Icon size={11} /> {lang === "ar" ? s.label_ar : s.label_en}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      className="btn btn-ghost"
                      onClick={() => startEdit(p)}
                      data-testid={`edit-position-${p.id}`}
                      title={t("edit")}
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      className="btn btn-ghost text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                      onClick={() => remove(p)}
                      disabled={p.is_default}
                      data-testid={`delete-position-${p.id}`}
                      title={p.is_default ? t("cannot_delete_default") : t("delete")}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
